import { useContext, useEffect, useRef, useState } from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Stack from '@mui/joy/Stack';
import ChatBubble from './ChatBubble';
import MessagesPaneHeader from './MessagesPaneHeader';
import AvatarWithStatus from './AvatarWithStatus';
import MessageInput from './MessageInput';
import ChatSchedule from '../calendar/ChatSchedule';
import { LayoutContext } from '../../context';
import { APIDeleteMessage, APIEditMessage, APIGetChatMessagesById, APIJoinChat, APIMarkMessagesAsRead, APISendMessage, createWebSocketConnection } from '../../api';
import { Alert, Button, Modal, ModalClose, ModalDialog, Snackbar, Typography } from '@mui/joy';

import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import { getAccessToken, apiGetMe } from '../../auth';
import { mergeMessages, newMessageId } from '../../messageState';



export default function MessagesPane({ chat, chats, setChats, setSearchParams, searchParams, setSelectedChat, heightOverride, onMessagesRead, onCloseChat }) {
  const { account, token, loadChats, loadsUnreadCounts, chatSettings } = useContext(LayoutContext);

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [textAreaValue, setTextAreaValue] = useState('');
  const [attachments, setAttachments] = useState([]);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const socketRef = useRef(null);
  const [wsConnectionStatus, setWsConnectionStatus] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [prevScrollHeight, setPrevScrollHeight] = useState(0);

  const [isJoining, setIsJoining] = useState(false);
  const [isMember, setIsMember] = useState(false);

  // === Состояния редактирования / удаления / ошибок ===
  const [editingMessage, setEditingMessage] = useState(null);
  const [deletingMessage, setDeletingMessage] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', color: 'danger' });
  // Доступность отправки в этот чат (из ответа /messages/): can_write + причина
  const [writeAccess, setWriteAccess] = useState({ can_write: true, reason: '', message: '' });

  // Является ли текущий аккаунт пациентом (для лимита символов и блокировки)
  const isPatientAccount = !!account?.type_profile?.includes?.('patient')
                           && !account?.type_profile?.includes?.('doctor')
                           && !account?.type_profile?.includes?.('admin')
                           && !account?.type_profile?.includes?.('moderator')
                           && !account?.type_profile?.includes?.('supervisor');

  // Лимиты берём из настроек чата (контекст загружает их с /api/main/chat-settings/).
  // На случай если контекст ещё не успел подгрузить — используем серверные дефолты.
  const patientMaxLength = chatSettings?.patient_message_max_length ?? 4000;
  const maxFileSizeMb    = chatSettings?.chat_max_file_size_mb     ?? 50;

  // Профиль заблокирован?
  const isBlocked = !!account?.is_block;

  // Проверяем, является ли чат "чужим" (только для наблюдения)
  const isStrangerChat = chat?.is_stranger;

  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const selectedIdRef = useRef(chat?.chat_id);
  selectedIdRef.current = chat?.chat_id;
  const epochRef = useRef(0);
  const historyBusyRef = useRef(false);
  const syncBusyRef = useRef(false);
  const syncRequestedRef = useRef(false);
  const historyCursorRef = useRef(null);
  const liveOverridesRef = useRef(new Map());
  const sendBusyRef = useRef(false);
  const retryRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [presenceById, setPresenceById] = useState({});
  const currentToken = () => getAccessToken() || tokenRef.current;
  const applyDangerSnapshot = (snapshot) => {
    if (!snapshot?.chat_id || Number(snapshot.chat_id) !== Number(selectedIdRef.current)) return;
    const patch = {
      is_dangerous: Boolean(snapshot.is_dangerous),
      danger_messages_count: Number(snapshot.danger_messages_count || 0),
      danger_manual: snapshot.danger_manual,
    };
    setChats?.((previous) => previous.map((item) =>
      Number(item.chat_id) === Number(snapshot.chat_id) ? { ...item, ...patch } : item));
    setSelectedChat?.((previous) => previous && Number(previous.chat_id) === Number(snapshot.chat_id)
      ? { ...previous, ...patch } : previous);
  };

  const applyDangerMessage = (message) => {
    if (!message?.id || Number(message.chat_id) !== Number(selectedIdRef.current)) return;
    const id = Number(message.id);
    if (liveOverridesRef.current.get(id) === null) return;
    const remembered = liveOverridesRef.current.get(id);
    const loaded = messagesRef.current.find((item) => Number(item.id) === id);
    const base = mergeMessages(loaded ? [loaded] : [], remembered ? [remembered] : []);
    const merged = mergeMessages(base, [message])[0];
    liveOverridesRef.current.set(id, merged);
    setMessages((previous) => mergeMessages(previous, [merged]));
    setChats?.((previous) => previous.map((item) =>
      Number(item.chat_id) === Number(message.chat_id) && Number(item.last_message?.id) === id
        ? { ...item, last_message: mergeMessages([item.last_message], [merged])[0] } : item));
  };

  const handleDangerChanged = (response) => {
    applyDangerMessage(response.message);
    applyDangerSnapshot(response.chat);
  };
  const updateAccess = (data) => setWriteAccess({
    can_write: data.can_write !== false,
    reason: data.write_block_reason || '', message: data.write_block_message || '',
  });

  const loadMessages = async (_offset = 0, limit = 30, initial = false) => {
    const id = chat?.chat_id;
    const epoch = epochRef.current;
    if (!id || historyBusyRef.current) return;
    historyBusyRef.current = true; setLoading(true);
    const container = messagesContainerRef.current;
    const oldHeight = container?.scrollHeight || 0;
    const oldTop = container?.scrollTop || 0;
    try {
      const params = !initial && historyCursorRef.current
        ? { before_id: historyCursorRef.current } : {};
      const data = await APIGetChatMessagesById(id, currentToken(), limit, 0, params);
      if (selectedIdRef.current !== id || epochRef.current !== epoch) return;
      const rows = data.messages || [];
      setMessages((previous) => {
        const merged = mergeMessages(previous, rows);
        return merged.filter((message) => liveOverridesRef.current.get(Number(message.id)) !== null);
      });
      historyCursorRef.current = data.next_before_id;
      setHasMore(!!data.has_more); updateAccess(data); setInitialLoad(false);
      requestAnimationFrame(() => {
        if (selectedIdRef.current !== id || epochRef.current !== epoch) return;
        if (initial) scrollToBottom();
        else if (container) container.scrollTop = oldTop + container.scrollHeight - oldHeight;
      });
    } catch (error) {
      if (selectedIdRef.current === id && epochRef.current === epoch)
        setSnackbar({ open: true, color: 'danger', message: error?.response?.data?.error || 'Не удалось загрузить сообщения' });
    } finally {
      if (selectedIdRef.current === id && epochRef.current === epoch) {
        historyBusyRef.current = false; setLoading(false);
        if (syncRequestedRef.current) { syncRequestedRef.current = false; synchronize(); }
      }
    }
  };

  const synchronize = async () => {
    const id = chat?.chat_id;
    const epoch = epochRef.current;
    if (!id || document.visibilityState !== 'visible') return;
    if (historyBusyRef.current || syncBusyRef.current) { syncRequestedRef.current = true; return; }
    const loaded = messagesRef.current;
    if (!loaded.length) { loadMessages(0, 30, true); return; }
    syncBusyRef.current = true; liveOverridesRef.current = new Map();
    let after = Math.min(...loaded.map((message) => Number(message.id))) - 1;
    let until;
    let rows = [];
    try {
      for (;;) {
        const params = { after_id: after };
        if (until != null) params.until_id = until;
        const data = await APIGetChatMessagesById(id, currentToken(), 100, 0, params);
        if (selectedIdRef.current !== id || epochRef.current !== epoch) return;
        until = data.sync_until_id;
        rows = mergeMessages(rows, data.messages || []);
        updateAccess(data);
        if (!data.has_more) break;
        if (!(data.next_after_id > after)) throw new Error('Invalid message cursor');
        after = data.next_after_id;
      }
      const overrides = new Map(liveOverridesRef.current);
      setMessages((previous) => {
        // Сверяем и изменения, и удаления в уже загруженном диапазоне.
        const fresh = mergeMessages(previous, rows);
        const actualIds = new Set(rows.map((message) => Number(message.id)));
        const minId = Math.min(...loaded.map((message) => Number(message.id)));
        const reconciled = fresh.filter((message) => {
          const mid = Number(message.id);
          return mid < minId || mid > until || actualIds.has(mid) || overrides.has(mid);
        });
        let result = reconciled;
        for (const [mid, message] of overrides) {
          result = message ? mergeMessages(result, [message]) : result.filter((item) => Number(item.id) !== mid);
        }
        return result;
      });
      loadsUnreadCounts?.();
    } catch (error) {
      if (selectedIdRef.current === id && epochRef.current === epoch)
        setSnackbar({ open: true, color: 'warning', message: 'Не удалось обновить историю. Повторим после восстановления связи.' });
    } finally {
      if (selectedIdRef.current === id && epochRef.current === epoch) {
        syncBusyRef.current = false;
        if (syncRequestedRef.current) { syncRequestedRef.current = false; synchronize(); }
      }
    }
  };

  useEffect(() => {
    epochRef.current += 1;
    historyBusyRef.current = false; syncBusyRef.current = false; syncRequestedRef.current = false;
    historyCursorRef.current = null; liveOverridesRef.current = new Map(); retryRef.current = null;
    setMessages([]); messagesRef.current = []; setPresenceById({});
    sendBusyRef.current = false; setSending(false);
    setHasMore(true); setInitialLoad(true); setEditingMessage(null);
    setTextAreaValue(''); setAttachments([]);
    if (chat?.chat_id) loadMessages(0, 30, true);
    return () => { epochRef.current += 1; };
  }, [chat?.chat_id]);

  useEffect(() => {
    if (!chat?.chat_id || !token) return;
    let disposed = false;
    const conn = createWebSocketConnection(chat.chat_id, currentToken, (packet) => {
      if (disposed) return;
      if (packet.type === 'presence') {
        setPresenceById(Object.fromEntries((packet.data || []).map((item) => [item.profile_id, item.is_online])));
        return;
      }
      const event = packet.data || {};
      const action = event.action;
      const message = event.message;
      if (action === 'danger_status_updated') {
        applyDangerSnapshot(event.event);
        return;
      }
      if (action === 'message_danger_updated') {
        applyDangerMessage(message);
        return;
      }
      if (action === 'messages_read') {
        if (Number(event.event?.profile_id) === Number(account?.id)) {
          const ids = new Set(event.event.message_ids || []);
          setMessages((previous) => previous.map((item) => ids.has(item.id) ? { ...item, is_read: true } : item));
        }
        return;
      }
      if (action === 'user_removed') {
        if (Number(event.event?.profile_id) === Number(account?.id))
          setWriteAccess({ can_write: false, reason: 'not_participant', message: 'Вы больше не участник чата' });
        loadChats?.().catch(() => {}); return;
      }
      if (!message?.id || Number(message.chat_id) !== Number(chat.chat_id)) return;
      if (action === 'message_deleted' || action === 'deleted') {
        liveOverridesRef.current.set(Number(message.id), null);
        setMessages((previous) => previous.filter((item) => Number(item.id) !== Number(message.id)));
        loadChats?.().catch(() => {}); return;
      }
      if (!['created', 'message_edited', 'updated', undefined].includes(action)) return;
      liveOverridesRef.current.set(Number(message.id), message);
      setMessages((previous) => mergeMessages(previous, [message]));
      setChats?.((previous) => previous.map((item) => {
        if (Number(item.chat_id) !== Number(chat.chat_id)) return item;
        const last = item.last_message;
        return !last?.id || Number(last.id) <= Number(message.id)
          ? { ...item, last_message: message } : item;
      }));
      const element = messagesContainerRef.current;
      if (element && element.scrollHeight - element.scrollTop - element.clientHeight < 150)
        requestAnimationFrame(scrollToBottom);
    }, (connected) => {
      if (disposed) return;
      setWsConnectionStatus(connected);
      if (!connected) setPresenceById({});
      else synchronize();
    }, { onAuthExpired: () => apiGetMe(), onWake: () => synchronize() });
    socketRef.current = conn;
    const poll = setInterval(() => {
      if (document.visibilityState === 'visible') synchronize();
    }, 60000);
    return () => { disposed = true; clearInterval(poll); conn.close(); socketRef.current = null; };
  }, [chat?.chat_id, token, account?.id, isStrangerChat]);

  // Прочитано только в видимой активной вкладке и в видимой области переписки.
  useEffect(() => {
    const root = messagesContainerRef.current;
    if (!root || !chat?.chat_id || isStrangerChat) return;
    const id = chat.chat_id;
    const epoch = epochRef.current;
    const pending = new Set();
    let timer;
    let busy = false;
    let disposed = false;
    const visible = () => document.visibilityState === 'visible' && document.hasFocus();
    const flush = async () => {
      if (disposed || busy || !visible() || !pending.size) return;
      busy = true;
      const ids = [...pending].slice(0, 250);
      ids.forEach((mid) => pending.delete(mid));
      try {
        const data = await APIMarkMessagesAsRead(id, ids, currentToken());
        if (selectedIdRef.current !== id || epochRef.current !== epoch) return;
        const accepted = new Set(data.message_ids || []);
        setMessages((previous) => previous.map((message) => accepted.has(message.id) ? { ...message, is_read: true } : message));
        loadsUnreadCounts?.(); onMessagesRead?.(id);
      } catch { /* Повторная попытка при следующем открытии/прокрутке. */ }
      finally { busy = false; if (!disposed && pending.size) timer = setTimeout(flush, 200); }
    };
    const observer = new IntersectionObserver((entries) => {
      if (!visible()) return;
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const mid = Number(entry.target.dataset.messageId);
        const message = messagesRef.current.find((item) => Number(item.id) === mid);
        if (message && !message.is_read && Number(message.sender?.id) !== Number(account?.id)) pending.add(message.id);
      }
      clearTimeout(timer); timer = setTimeout(flush, 200);
    }, { root, threshold: 0.1 });
    const observe = () => {
      observer.disconnect();
      if (visible()) root.querySelectorAll('[data-message-id]').forEach((node) => observer.observe(node));
    };
    observe();
    document.addEventListener('visibilitychange', observe); window.addEventListener('focus', observe);
    return () => {
      disposed = true; clearTimeout(timer); observer.disconnect();
      document.removeEventListener('visibilitychange', observe); window.removeEventListener('focus', observe);
    };
  }, [chat?.chat_id, messages, account?.id, isStrangerChat]);

  const handleScroll = (event) => {
    if (event.currentTarget.scrollTop < 100 && !loading && hasMore && !initialLoad) loadMoreMessages();
  };
  const loadMoreMessages = async () => {
    if (!loading && hasMore) await loadMessages(0, 30, false);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (sendBusyRef.current) return false;
    const id = chat?.chat_id;
    const epoch = epochRef.current;
    const content = textAreaValue.trim();
    if (!id || (!content && !attachments.length)) return false;
    sendBusyRef.current = true; setSending(true);
    try {
      if (editingMessage) {
        const updated = await APIEditMessage(editingMessage.id, content, currentToken());
        if (selectedIdRef.current !== id || epochRef.current !== epoch) return true;
        liveOverridesRef.current.set(Number(updated.id), updated);
        setMessages((previous) => mergeMessages(previous, [updated]));
        setEditingMessage(null); setTextAreaValue('');
        return true;
      }
      const signature = JSON.stringify([id, content,
        attachments.map((file) => [file.name, file.size, file.lastModified])]);
      if (!retryRef.current || retryRef.current.signature !== signature)
        retryRef.current = { signature, nonce: newMessageId() };
      const formData = new FormData();
      formData.append('content', content);
      formData.append('client_message_id', retryRef.current.nonce);
      attachments.forEach((file) => formData.append('attachments', file));
      const saved = await APISendMessage(id, formData, currentToken());
      if (selectedIdRef.current !== id || epochRef.current !== epoch) return true;
      liveOverridesRef.current.set(Number(saved.id), saved);
      setMessages((previous) => mergeMessages(previous, [saved]));
      setChats?.((previous) => previous.map((item) => {
        if (Number(item.chat_id) !== Number(id)) return item;
        const last = item.last_message;
        return !last?.id || Number(last.id) <= Number(saved.id)
          ? { ...item, last_message: saved } : item;
      }));
      retryRef.current = null; setTextAreaValue(''); setAttachments([]);
      requestAnimationFrame(scrollToBottom);
      loadsUnreadCounts?.();
      return true;
    } catch (error) {
      if (selectedIdRef.current === id && epochRef.current === epoch) {
        const data = error?.response?.data;
        setSnackbar({ open: true, color: 'danger', message: data?.error || 'Не удалось отправить сообщение. Текст сохранён — можно повторить.' });
        if (error?.response?.status === 403 && data?.reason)
          setWriteAccess({ can_write: false, reason: data.reason, message: data.error || '' });
      }
      return false;
    } finally {
      if (selectedIdRef.current === id && epochRef.current === epoch) {
        sendBusyRef.current = false; setSending(false);
      }
    }
  };

  // === Действия с конкретным сообщением ===
  const handleStartEdit = (msg) => {
    if (!msg) return;
    setEditingMessage(msg);
    setTextAreaValue(msg.content || '');
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    setTextAreaValue('');
  };

  const handleConfirmDelete = async () => {
    if (!deletingMessage) return;
    const mid = Number(deletingMessage.id);
    const id = chat?.chat_id;
    const epoch = epochRef.current;
    try {
      await APIDeleteMessage(mid, currentToken());
      if (selectedIdRef.current !== id || epochRef.current !== epoch) return;
      liveOverridesRef.current.set(mid, null);
      setMessages((previous) => previous.filter((message) => Number(message.id) !== mid));
      setDeletingMessage(null);
      loadChats?.().catch(() => {}); loadsUnreadCounts?.();
      setSnackbar({ open: true, message: 'Сообщение удалено', color: 'success' });
    } catch (error) {
      if (selectedIdRef.current === id && epochRef.current === epoch)
        setSnackbar({ open: true, message: error?.response?.data?.error || 'Не удалось удалить сообщение', color: 'danger' });
    }
  };

  const handleAttachFiles = (files) => {
    retryRef.current = null;
    setAttachments(files);
  };
  
  const handleUserAdded = () => {
    // // Перезагрузить чаты или обновить текущий чат
    // APIGetChats(token).then(data => {
    //   setChats(data?.chats);
    //   // Обновить текущий выбранный чат
    //   const updatedChat = data?.chats.find(c => c.chat_id === selectedChatId);
    //   if (updatedChat) {
    //     setSelectedChat(updatedChat);
    //   }
    // });
  };

  // Проверяем, является ли пользователь участником чата
  useEffect(() => {
    if (chat && account) {
      // Проверяем, есть ли текущий пользователь в участниках чата
      const member = chat.is_member ?? !!chat.participants_with_roles?.some(
        participant => participant.profile.id === account.id
      );
      setIsMember(member);
    }
  }, [chat, account]);
  
  const handleJoinChat = async () => {
    if (!chat?.chat_id || isJoining) return;
    
    try {
      setIsJoining(true);
      const response = await APIJoinChat(chat.chat_id, token);
      
      if (response.success) {
        alert(response.message);
        setIsMember(true);

        // Снимаем блокировку поля ввода сразу — плашка "не участник" больше не актуальна.
        // Если бэкенд вернул актуальный доступ (can_write) — берём его; иначе оптимистично
        // разрешаем запись и перечитываем сообщения, чтобы забрать точный доступ с сервера.
        if (typeof response.can_write !== 'undefined') {
          setWriteAccess({
            can_write: response.can_write !== false,
            reason: response.write_block_reason || '',
            message: response.write_block_message || '',
          });
        } else {
          setWriteAccess({ can_write: true, reason: '', message: '' });
          loadMessages(0, 30, true);
        }
        
        // Обновляем список чатов
        await loadChats();
        
        // Обновляем текущий чат: добавляем себя И в participants, И в participants_with_roles
        // (isMember считается именно по participants_with_roles), снимаем флаг "чужого" чата.
        if (setSelectedChat) {
          setSelectedChat(prev => {
            const roles = prev?.participants_with_roles || [];
            const alreadyInRoles = roles.some(p => p?.profile?.id === account.id);
            return {
              ...prev,
              participants: [...(prev.participants || []), {
                id: account.id,
                name: account.name,
                profile: account
              }],
              participants_with_roles: alreadyInRoles ? roles : [
                ...roles,
                { profile: account, role: response.role || 'member' }
              ],
              is_stranger: false
            };
          });
        }
      }
    } catch (error) {
      console.error('Ошибка при присоединении к чату:', error);
      alert(error.response?.data?.error || 'Ошибка присоединения', { variant: 'error' });
    } finally {
      setIsJoining(false);
    }
  };

  return (
    <Sheet
      sx={{
        height: heightOverride ?? { 
          xs: '100dvh',
          sm: 'calc(96.5dvh - var(--Header-height))', 
          md: '100dvh'
        },
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.level1',
        minHeight: 0,
        width: '100%',
        overflow: 'hidden',
      }}
    >
      { chat && (
        <>
          <MessagesPaneHeader chat={chat} wsConnected={wsConnectionStatus} searchParams={searchParams} setSelectedChat={setSelectedChat} setSearchParams={setSearchParams} onUserAdded={handleUserAdded} setChats={setChats} onCloseChat={onCloseChat} />
          <ChatSchedule key={chat.chat_id} chatId={chat.chat_id} presenceById={presenceById} />
          
          {/* Блок для "чужого" чата - только наблюдение */}
          {isStrangerChat && (
            <Alert
              color="danger" 
              variant="soft" 
              sx={{ mx: 2, my: 1, borderRadius: 'lg' }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={{ xs: 0, sm: 1 }} sx={{ width: '100%' }}>
                <Typography level="body-sm" fontWeight="bold">
                  🔍 Режим наблюдения
                </Typography>
                <Typography level="body-xs">
                  Вы можете только просматривать этот чат
                </Typography>
              </Stack>
            </Alert>
          )}

          <Box
            ref={messagesContainerRef}
            onScroll={handleScroll}
            sx={{
              display: 'flex',
              flex: 1,
              minHeight: 0,
              px: { xs: 1.5, sm: 2.5 },
              py: 2,
              overflowY: 'auto',
              overflowX: 'hidden',
              overscrollBehaviorY: 'contain',
              scrollbarWidth: 'thin',
              flexDirection: 'column',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': { borderRadius: '6px', bgcolor: 'neutral.300' },
            }}
          >
            {loading && (
              <Typography textAlign="center" color="neutral">
                Загрузка...
              </Typography>
            )}
            <Stack spacing={2} sx={{ justifyContent: 'flex-end' }}>
              {messages?.map((message) => {
                return (
                  <Stack
                    key={message?.id}
                    data-message-id={message?.id}
                    direction="row"
                    spacing={2}
                    sx={{ 
                      flexDirection: account?.id == message?.sender?.id ? 'row-reverse' : 'row',
                      justifyContent: message?.type_sender == "system" ? "center" : "normal"
                    }}
                  >
                    {account?.id != message?.sender?.id && message?.type_sender != "system" && (
                      <AvatarWithStatus
                        online={presenceById[message.sender?.id]}
                      />
                    )}
                    <ChatBubble
                      message={message}
                      isYou={account?.id == message?.sender?.id}
                      onEdit={handleStartEdit}
                      onDelete={(m) => setDeletingMessage(m)}
                      onDangerChanged={handleDangerChanged}
                    />
                  </Stack>
                );
              })}
              <div ref={messagesEndRef} />
            </Stack>
          </Box>

          {/* Заменяем MessageInput на блок с предупреждением для "чужого" чата */}
          {isStrangerChat ? (
            <Box sx={{ px: 2, pb: 2 }}>
              <Sheet
                variant="plain" 
                // color="neutral" 
                sx={{ 
                  p: 2, 
                  borderRadius: 'lg',
                  textAlign: 'center'
                }}
              >
                <Stack spacing={1} direction={{ xs: 'column', sm: 'row' }} justifyContent={'center'} alignItems={'center'}>
                  <Typography level="body-sm" color="neutral">
                    👀 Вы можете только наблюдать за этим чатом
                  </Typography>
                  {/* Индикатор статуса участника */}
                  {chat?.is_stranger && !isMember && (
                    <Button
                      variant="soft"
                      color="primary"
                      size="sm"
                      startDecorator={<LoginIcon sx={{ fontSize: 16 }} />}
                      onClick={handleJoinChat}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'warning.50' }
                      }}
                    >
                      Присоединиться
                    </Button>
                  )}
                </Stack>
                
              </Sheet>
            </Box>
          ) : (
            <MessageInput
              key={chat.chat_id}
              textAreaValue={textAreaValue}
              setTextAreaValue={setTextAreaValue}
              onSubmit={handleSendMessage}
              onAttach={handleAttachFiles}
              editingMessage={editingMessage}
              onCancelEdit={handleCancelEdit}
              maxLength={isPatientAccount ? patientMaxLength : null}
              maxFileSizeMb={maxFileSizeMb}
              sending={sending}
              disabled={sending || isBlocked || !writeAccess.can_write}
              disabledReason={isBlocked ? (account?.block_reason || '') : writeAccess.message}
            />
          )}

          {/* === Модалка подтверждения удаления сообщения === */}
          {deletingMessage && (
            <Modal open onClose={() => setDeletingMessage(null)} sx={{ zIndex: 10014 }}>
              <ModalDialog size="sm">
                <ModalClose />
                <Typography level="title-md" sx={{ mb: 1 }}>
                  Удалить сообщение?
                </Typography>
                <Typography level="body-sm" sx={{ mb: 2, opacity: 0.8 }}>
                  Действие нельзя отменить. Сообщение будет скрыто из чата.
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button variant="plain" color="neutral" onClick={() => setDeletingMessage(null)}>
                    Отмена
                  </Button>
                  <Button color="danger" onClick={handleConfirmDelete}>
                    Удалить
                  </Button>
                </Stack>
              </ModalDialog>
            </Modal>
          )}

          {/* === Snackbar для ошибок отправки/редактирования/удаления === */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar(s => ({ ...s, open: false }))}
            color={snackbar.color}
            variant="soft"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            sx={{ zIndex: 10015 }}
          >
            {snackbar.message}
          </Snackbar>
        </>
      )}
    </Sheet>
  );
}
